import { Outlet, useLocation } from 'react-router-dom';
import { Brand } from './Brand';
import { ProfileMenu } from './ProfileMenu';
import { useAuth } from '@/auth/AuthProvider';
import { useT } from '@/i18n/useT';
import { useBotAlerts } from '@/bot/hooks';
import { StopWinCelebration } from '@/features/bot/StopWinCelebration';

/** The five bot screens. The approved appbar showed the connection pill on
 *  these and only these, so the pill is not fetched on billing/admin/account. */
const BOT_SCREENS = ['/dashboard', '/trading', '/strategy', '/history', '/bot-settings'];

/**
 * Signed-in app shell: the approved header is the $mallX logo and tagline on the
 * left, the account menu on the right, and nothing else.
 *
 * No back arrow: it was not in the approved design, and the bottom nav already
 * moves between every screen, so the arrow only competed with the logo for the
 * space the brand is supposed to own.
 */
export function AppLayout() {
  const { user } = useAuth();
  const { t } = useT();
  const { pathname } = useLocation();
  // Same query key as the dashboard's own call, so this shares the cache
  // rather than adding a second poll.
  const onBotScreen = BOT_SCREENS.some((p) => pathname.startsWith(p));
  const alerts = useBotAlerts(onBotScreen);
  // Offline if the bot itself says the session is down OR the fetch failed
  // (TanStack keeps the last data on a failed refetch, so isError is the only
  // signal that the bot is unreachable right now).
  const sessionOk = alerts.data?.health?.sessionOk !== false && !alerts.isError;

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <div className="topbar-left">
          {/* Full-size brand. The approved design leads with the $mallX logo and
              tagline as the header, not a compact strip beside a back arrow. */}
          <a href="/app/dashboard" className="brand-link" aria-label={t('a11y.dashboard')}>
            <Brand />
          </a>
        </div>
        {/* The connection pill belongs IN the appbar between the brand and the
            account menu, as in public/index.html. 4B-2 dropped it onto its own
            right-aligned row above the balance card, which is the floating
            "CONNECTED" line that does not exist in the approved design. */}
        {onBotScreen && alerts.data ? (
          <span className={`bot-conn ${sessionOk ? 'ok' : 'down'}`}>
            <i />
            {sessionOk ? t('dash.connected') : t('dash.offline')}
          </span>
        ) : null}
        <ProfileMenu />
      </header>
      <main className="app-main">
        {/* Nav clearance only where the bottom nav actually renders; account,
            billing, admin and connect have no nav and no need for 104px of
            dead space under their content. */}
        <div className={onBotScreen ? 'app-content app-content--nav' : 'app-content'}>{user ? <Outlet /> : null}</div>
      </main>
      {/* Stop-Win celebration. Mounted at the shell so it fires on whichever bot
          screen the customer happens to be on, exactly as the approved
          dashboard did (it polled globally). Only on bot screens, so it shares
          the /state cache and adds no request. */}
      {onBotScreen ? <StopWinCelebration /> : null}
    </div>
  );
}
